import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import type { Publication } from "../publications.types";
import { publicationThumbnailUrl } from "../publicationFileUrl";
import { LoginForm } from "./LoginForm";
import { PublicationForm } from "./PublicationForm";
import {
  createPublicationRequest,
  deletePublicationRequest,
  fetchPublications,
  updatePublicationRequest,
  type PublicationFormValues,
} from "./publicationsAdminApi";
import styles from "./PublicationsAdmin.module.css";

const TOKEN_KEY = "megs_admin_token";

type EditorState =
  | { mode: "closed" }
  | { mode: "new" }
  | { mode: "edit"; publication: Publication };

export const PublicationsAdmin = () => {
  const [token, setToken] = useState<string | null>(() =>
    sessionStorage.getItem(TOKEN_KEY),
  );
  const [publications, setPublications] = useState<Array<Publication>>([]);
  const [editor, setEditor] = useState<EditorState>({ mode: "closed" });

  const signOut = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const refresh = useCallback(async () => {
    const data = await fetchPublications();
    setPublications(data);
  }, []);

  const handleAuthenticated = (newToken: string) => {
    sessionStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  };

  useEffect(() => {
    if (token !== null) {
      void refresh();
    }
  }, [token, refresh]);

  const withSession = async (action: () => Promise<void>): Promise<void> => {
    try {
      await action();
      await refresh();
    } catch (error) {
      // Token expired / invalid: force re-login.
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        signOut();
        return;
      }
      throw error;
    }
  };

  const handleCreate = (values: PublicationFormValues) =>
    withSession(async () => {
      await createPublicationRequest(token ?? "", values);
      setEditor({ mode: "closed" });
    });

  const handleUpdate = (id: number, values: PublicationFormValues) =>
    withSession(async () => {
      await updatePublicationRequest(token ?? "", id, values);
      setEditor({ mode: "closed" });
    });

  const handleDelete = (publication: Publication) => {
    const confirmed = window.confirm(
      `Delete "${publication.title}"? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }
    void withSession(() =>
      deletePublicationRequest(token ?? "", publication.id),
    );
  };

  if (token === null) {
    return (
      <div className={styles.container}>
        <LoginForm onAuthenticated={handleAuthenticated} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>Publications admin</h1>
        <div className={styles.headerActions}>
          <Link to="/publications">View site</Link>
          <button type="button" className={styles.secondary} onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      {editor.mode === "closed" && (
        <button
          type="button"
          className={styles.addButton}
          onClick={() => setEditor({ mode: "new" })}
        >
          Add publication
        </button>
      )}

      {editor.mode === "new" && (
        <section className={styles.editorPanel}>
          <h2>New publication</h2>
          <PublicationForm
            submitLabel="Create"
            onSubmit={handleCreate}
            onCancel={() => setEditor({ mode: "closed" })}
          />
        </section>
      )}

      {editor.mode === "edit" && (
        <section className={styles.editorPanel}>
          <h2>Edit publication</h2>
          <PublicationForm
            initial={editor.publication}
            submitLabel="Save changes"
            onSubmit={(values) => handleUpdate(editor.publication.id, values)}
            onCancel={() => setEditor({ mode: "closed" })}
          />
        </section>
      )}

      <ul className={styles.list}>
        {publications.map((publication) => (
          <li key={publication.id} className={styles.listItem}>
            <img
              src={publicationThumbnailUrl(publication.thumbnail)}
              alt=""
              className={styles.listThumbnail}
            />
            <div className={styles.listBody}>
              <h3>{publication.title}</h3>
              <p>{publication.description.slice(0, 160)}…</p>
            </div>
            <div className={styles.listActions}>
              <button
                type="button"
                onClick={() =>
                  setEditor({ mode: "edit", publication })
                }
              >
                Edit
              </button>
              <button
                type="button"
                className={styles.danger}
                onClick={() => handleDelete(publication)}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
